import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { professionClassifier, type ProfessionClassifierEntry } from '@/api/hr';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

export default function ProfessionClassifierPage() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 100;
  const list = useQuery({
    queryKey: ['profession-classifier', q, offset],
    queryFn: () => professionClassifier.list({ q: q || undefined, limit, offset }),
  });

  const total = list.data?.total ?? 0;
  const items = list.data?.items ?? [];

  const cols = useMemo<ColumnDef<ProfessionClassifierEntry>[]>(() => [
    {
      key: 'kp_code', header: 'Код КП', width: 90,
      sortValue: (p) => p.kp_code ?? '',
      cell: (p) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{p.kp_code}</span>
      ),
    },
    {
      // Без width — гнучка колонка: займає вільний простір.
      key: 'professional_name', header: 'Професійна назва роботи',
      sortValue: (p) => (p.professional_name ?? '').toLowerCase(),
      cell: (p) => p.professional_name,
    },
    {
      key: 'name', header: 'Група',
      sortValue: (p) => (p.name ?? '').toLowerCase(),
      cell: (p) => (
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
          {p.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'zkpptr_code', header: 'ЗКППТР', width: 110,
      sortValue: (p) => p.zkpptr_code ?? '',
      cell: (p) => (
        <span style={{ color: 'var(--muted)', fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {p.zkpptr_code ?? '—'}
        </span>
      ),
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Класифікатор професій ДК 003:2010</h2>
        <input placeholder="Код або назва професії…" value={q}
               onChange={(e) => { setQ(e.target.value); setOffset(0); }}
               style={{ width: 160, flex: '0 0 auto' }} />
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem', marginLeft: 'auto' }}>
          Знайдено: <strong>{total}</strong>
        </span>
      </div>

      {total === 0 && !list.isLoading && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>
          {q ? 'Нічого не знайдено.' : (
            <>
              Класифікатор порожній. Завантажте через
              <a href="/import-csv"> Імпорт CSV</a>.
            </>
          )}
        </div>
      )}

      {(items.length > 0 || list.isLoading) && (
        <DataTable<ProfessionClassifierEntry>
          rows={items}
          columns={cols}
          rowKey={(p) => p.id}
          loading={list.isLoading}
          emptyText="Нічого не знайдено."
          rowMuted={(p) => p.deletion_mark ? 0.4 : undefined}
          initialSortKey="kp_code"
        />
      )}

      {total > limit && (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>
            ← Назад
          </button>
          <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            {offset + 1}–{Math.min(offset + limit, total)} з {total}
          </span>
          <button disabled={offset + limit >= total}
                  onClick={() => setOffset(offset + limit)}>
            Вперед →
          </button>
        </div>
      )}
    </div>
  );
}
